import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../utils/translations';
import { ArrowRight, Package, Zap, BarChart3 } from 'lucide-react';

const UseCases = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const caseIcons = {
    inventory: Package,
    automation: Zap,
    visibility: BarChart3
  };

  return (
    <section className="py-16 md:py-24 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4 md:px-6">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center px-4 py-2 rounded-full text-sm font-medium mb-6" style={{ backgroundColor: '#e0f2fe', color: '#115d7b' }}>
            <BarChart3 className="w-4 h-4 mr-2" />
            {language === 'en' ? 'Real Solutions' : 'Soluciones Reales'}
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {t.useCases.title}
          </h2>
          <p className="text-xl text-gray-600">
            {t.useCases.subtitle}
          </p>
        </div>

        <style dangerouslySetInnerHTML={{__html: `
          .scrollbar-none::-webkit-scrollbar {
            display: none;
          }
          .scrollbar-none {
            -ms-overflow-style: none;
            scrollbar-width: none;
          }
        `}} />
        <div className="flex overflow-x-auto md:grid md:grid-cols-3 gap-6 md:gap-8 max-w-7xl mx-auto snap-x snap-mandatory scrollbar-none pb-6 md:pb-0 -mx-4 px-4 md:mx-0 md:px-0">
          {t.useCases.cases.map((useCase, index) => {
            const IconComponent = caseIcons[useCase.icon as keyof typeof caseIcons];

            return (
              <div
                key={index}
                className="w-[85vw] md:w-full flex-shrink-0 md:flex-shrink snap-start group bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden transform hover:-translate-y-2"
              >
                {/* Challenge Section */}
                <div className="p-8 bg-gradient-to-br from-red-50 to-orange-50 border-b-4 border-red-200">
                  <div className="flex items-start mb-4">
                    <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center mr-4 flex-shrink-0">
                      <span className="text-2xl">⚠️</span>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-red-600 uppercase tracking-wide mb-2">
                        {language === 'en' ? 'Challenge' : 'Reto'}
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {useCase.challenge}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm">
                    {useCase.challengeDescription}
                  </p>
                </div>

                {/* Arrow Divider */}
                <div className="flex justify-center py-4 bg-white">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300" style={{ backgroundColor: '#e0f2fe' }}>
                    <ArrowRight className="w-6 h-6" style={{ color: '#115d7b' }} />
                  </div>
                </div>

                {/* Solution Section */}
                <div className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50">
                  <div className="flex items-start mb-4">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center mr-4 flex-shrink-0" style={{ backgroundColor: '#115d7b' }}>
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold uppercase tracking-wide mb-2" style={{ color: '#115d7b' }}>
                        {language === 'en' ? 'Solution' : 'Solución'}
                      </h3>
                      <p className="text-xl font-bold text-gray-900">
                        {useCase.solution}
                      </p>
                    </div>
                  </div>
                  <p className="text-gray-600 text-sm mb-6">
                    {useCase.solutionDescription}
                  </p>

                  {/* Impact Metric */}
                  <div className="mt-6 pt-6 border-t border-blue-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-3xl font-bold" style={{ color: '#115d7b' }}>
                          {useCase.impact}
                        </div>
                        <div className="text-gray-500 text-xs mt-1">
                          {useCase.impactLabel}
                        </div>
                      </div>
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: '#115d7b' }}>
                        <span className="text-xl">✓</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Hover Effect Overlay */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/0 to-cyan-600/0 group-hover:from-blue-600/5 group-hover:to-cyan-600/5 transition-all duration-500 pointer-events-none"></div>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="text-center mt-16">
          <p className="text-lg text-gray-600 mb-6">
            {language === 'en'
              ? 'Discover how Laimu can solve your specific business challenges'
              : 'Descubre cómo Laimu puede resolver tus desafíos empresariales específicos'
            }
          </p>
          <a
            href="/demo"
            className="inline-flex items-center px-8 py-4 text-white rounded-lg transition-all duration-300 font-bold text-lg shadow-xl hover:shadow-2xl transform hover:-translate-y-1"
            style={{ backgroundColor: '#115d7b' }}
            onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#0f5269')}
            onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = '#115d7b')}
          >
            {language === 'en' ? 'Request a Demo' : 'Solicitar una Demo'}
            <ArrowRight className="ml-2 h-5 w-5" />
          </a>
        </div>
      </div>
    </section>
  );
};

export default UseCases;
